/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import * as AppPropTypes from '../../lib/PropTypes';
import { isExternalUrl } from '../../lib/utils';

import { useUser } from '../../contexts/AuthContext';
import { useEdition } from '../../contexts/EditionContext';
import { getPurchaseByProduct } from '../../contexts/PurchasesContext';
import { useUrlGenerator } from '../../contexts/RoutesContext';
import CircledPlusIcon from '../icons/CircledPlus';
import SmallTicketIcon from '../icons/SmallTicket';
import WatchIcon from '../icons/Watch';
import IconLink from '../partials/IconLink';

import styles from '../../../styles/buttons/event-tickets.module.scss';

const propTypes = {
    item: AppPropTypes.event.isRequired,
    children: PropTypes.node,
    isDark: PropTypes.bool,
    className: PropTypes.string,
};

const defaultProps = {
    children: null,
    isDark: false,
    className: null,
};

// NOTE: IMPORTANT: this is for items with a product, think about a refactor (booking details, card, etc)

const EventTickets = ({ item, isDark, children, className, ...props }) => {
    const route = useUrlGenerator();
    const user = useUser();
    const { ended = false } = useEdition() || {};
    const {
        ticketsUrl = null,
        isOnline = false,
        product = null,
        isPurchasable = true,
        isEnded = false, // They have to put end dates in the database to enable this reliably
    } = item || {};
    const purchase = getPurchaseByProduct(product);
    const { type: productType = null, url: productUrl = null } = product || {};

    // FIXME label handling is very very iffy -> a refactor should happen
    const buyLabel =
        productType === 'forum' ? (
            <FormattedMessage defaultMessage="Buy accreditation" description="Button label" />
        ) : (
            <FormattedMessage defaultMessage="Buy" description="Button label" />
        );
    let viewLabel =
        productType === 'forum' ? (
            <FormattedMessage defaultMessage="Access forum page" description="Button label" />
        ) : (
            <FormattedMessage defaultMessage="View your ticket" description="Button label" />
        );
    viewLabel = isOnline ? (
        <FormattedMessage defaultMessage="Watch online" description="Button label" />
    ) : (
        viewLabel
    );
    const hasPurchaseAndProduct = purchase !== null && productUrl;
    const finalLabel = hasPurchaseAndProduct ? viewLabel : buyLabel;

    let viewButton = productType === 'forum' ? <CircledPlusIcon /> : <SmallTicketIcon />; // if forum, it's a link to a page, otherwise "view tickets"
    viewButton = isOnline && productUrl ? <WatchIcon /> : viewButton; // if it's online and we have a URL, show "watch" icon for the "view" mode
    const buttonIcon = purchase !== null && productUrl ? viewButton : <SmallTicketIcon />; // if it's purchased already, display the "view" button, otherwise, the "buy" button

    const buttonUrl = isOnline && productUrl && purchase !== null ? productUrl : ticketsUrl;
    const external = isExternalUrl(buttonUrl);
    const finalUrl =
        !external && user === null
            ? `${route('login')}?next=${encodeURIComponent(ticketsUrl)}&tickets=1`
            : buttonUrl;

    // Never show something without a product, could be an event without any tickets
    if (productUrl === null) {
        return null;
    }

    if (isOnline && ended) {
        return null;
    }

    return !isPurchasable ? (
        <IconLink
            className={classNames([styles.container, { [className]: className !== null }])}
            label={
                isEnded ? (
                    <FormattedMessage defaultMessage="Ended" description="Button label" />
                ) : (
                    <FormattedMessage defaultMessage="Not available" description="Button label" />
                )
            }
            disabled
        />
    ) : (
        <IconLink
            className={classNames([
                styles.container,
                styles.purchaseable,
                { [styles.isDark]: isDark, [className]: className !== null },
            ])}
            icon={buttonIcon}
            isDark={isDark}
            href={finalUrl}
            external={external}
            label={children || finalLabel}
            {...props}
        />
    );
};

EventTickets.propTypes = propTypes;
EventTickets.defaultProps = defaultProps;

export default EventTickets;
